/*
 * ascii-logo.js
 * -------------
 * A small, self-contained "live ASCII" logo. Loads an .stl model, software-
 * renders it to ASCII characters, spins it on its own, and lets the visitor
 * grab + drag to orbit it (with inertia). On every page load it picks a
 * different character set than the previous load.
 *
 * No dependencies, no build step. Drop it on an element:
 *
 *   <span class="ascii-logo" data-ascii-logo
 *         data-stl="/path/to/logo.stl"
 *         data-cell="5" data-spin="32"></span>
 *
 * Config (all optional except data-stl):
 *   data-stl     : URL of the .stl model (required)
 *   data-cell    : character cell width in px (smaller = denser). default 5
 *   data-spin    : idle auto-spin speed in deg/sec. default 32
 *   data-color   : glyph colour. default '' = inherit the element's CSS color
 *   data-charset : a fixed ramp "dark->light" (disables the per-load shuffle)
 *   data-size    : force a square px size (else it fills the element's box)
 *   data-fps     : render cap. default 30
 */
(function () {
  'use strict';

  // ---- character ramps (dark -> light). One is chosen per page load. -------
  var RAMPS = [
    // 'dots', 'braille' + 'circles' removed — sparse round glyphs don't give
    // enough visual fidelity for logo silhouettes.
    [' .:-=+*#%@', 'classic'],
    [' .oO0@', 'blocky'],
    [' ░▒▓█', 'shaded'],          //  ░▒▓█
    [' ▁▂▃▄▅▆▇█', 'bars'], // ▁▂▃▄▅▆▇█
    [' .,:;clxokXdO0KN', 'type'],
    [' 01', 'binary']
  ];
  var LS_KEY = 'asciiLogoRamp'; // remembers the last ramp name so we don't repeat
  var EN_KEY = 'asciiLogoEnabled'; // '0' = effect off (persisted across pages)

  function isEnabled() {
    try { return window.localStorage.getItem(EN_KEY) !== '0'; } catch (e) { return true; }
  }

  // Fine, high-gradation ramps only — phones render at a coarser cell size, and
  // the blocky/shaded/bars/binary ramps turn into ugly chunky blocks there.
  var FINE_RAMPS = ['classic', 'type'];

  // Where a mobile logo comes to rest after its intro: front-facing, turned a
  // little to the left (radians) so the 3D extrusion reads instead of landing
  // flat or backwards.
  var LANDING_YAW = -0.22;

  function pickRamp(el) {
    var mobile = !!(window.matchMedia && window.matchMedia('(max-width: 749px)').matches);
    var fixed = el.getAttribute('data-charset');
    // On mobile, ignore blocky fixed/allow choices entirely and force a fine ramp.
    if (fixed && !mobile) {
      // accept a ramp NAME (e.g. "shaded") as well as a literal ramp string, so
      // templates don't need to embed the Unicode glyphs.
      for (var r = 0; r < RAMPS.length; r++) { if (RAMPS[r][1] === fixed) { return RAMPS[r][0]; } }
      return fixed;
    }
    // data-ramps="shaded,bars,..." restricts the per-load random pool to a
    // subset (wordmark collection logos use this to avoid sparse/detailed
    // ramps that render illegibly). Names not in RAMPS are ignored.
    var base = RAMPS;
    if (mobile) {
      base = RAMPS.filter(function (r) { return FINE_RAMPS.indexOf(r[1]) > -1; });
    } else {
      var allow = el.getAttribute('data-ramps');
      if (allow) {
        var names = allow.split(',');
        var filtered = RAMPS.filter(function (r) { return names.indexOf(r[1]) > -1; });
        if (filtered.length) { base = filtered; }
      }
    }
    var last = null;
    try { last = window.localStorage.getItem(LS_KEY); } catch (e) {}
    var pool = base.filter(function (r) { return r[1] !== last; });
    if (!pool.length) { pool = base; }
    var chosen = pool[Math.floor(Math.random() * pool.length)];
    try { window.localStorage.setItem(LS_KEY, chosen[1]); } catch (e2) {}
    return chosen[0];
  }

  // tiny deterministic value-noise (from the ascii-background tool's "boil")
  function hash(x, y, t) {
    var n = Math.sin(x * 12.9898 + y * 78.233 + t * 0.137) * 43758.5453;
    return n - Math.floor(n); // 0..1
  }

  // ---- STL parsing (ASCII + binary) ---------------------------------------
  function parseSTL(buf) {
    var dv = new DataView(buf);
    var binTris = buf.byteLength >= 84 ? dv.getUint32(80, true) : 0;
    var isBinary = buf.byteLength === 84 + binTris * 50 && binTris > 0;
    if (isBinary) {
      var v = new Float32Array(binTris * 9), off = 84, j = 0;
      for (var i = 0; i < binTris; i++) {
        off += 12; // skip the file's face normal
        for (var k = 0; k < 9; k++) { v[j++] = dv.getFloat32(off, true); off += 4; }
        off += 2; // attribute byte count
      }
      return v;
    }
    var text = new TextDecoder().decode(buf);
    var re = /vertex\s+(-?[\d.eE+]+)\s+(-?[\d.eE+]+)\s+(-?[\d.eE+]+)/g, m, arr = [];
    while ((m = re.exec(text))) { arr.push(+m[1], +m[2], +m[3]); }
    return new Float32Array(arr);
  }

  // Center + uniformly scale to ~[-1,1], compute per-face normals.
  function prepMesh(v) {
    var n = (v.length / 9) | 0, i;
    var minx = Infinity, miny = Infinity, minz = Infinity, maxx = -Infinity, maxy = -Infinity, maxz = -Infinity;
    for (i = 0; i < v.length; i += 3) {
      if (v[i] < minx) minx = v[i]; if (v[i] > maxx) maxx = v[i];
      if (v[i + 1] < miny) miny = v[i + 1]; if (v[i + 1] > maxy) maxy = v[i + 1];
      if (v[i + 2] < minz) minz = v[i + 2]; if (v[i + 2] > maxz) maxz = v[i + 2];
    }
    var cx = (minx + maxx) / 2, cy = (miny + maxy) / 2, cz = (minz + maxz) / 2;
    var ext = Math.max(maxx - minx, maxy - miny, maxz - minz) || 1, s = 2 / ext;
    var pos = new Float32Array(v.length);
    for (i = 0; i < v.length; i += 3) {
      pos[i] = (v[i] - cx) * s; pos[i + 1] = (v[i + 1] - cy) * s; pos[i + 2] = (v[i + 2] - cz) * s;
    }
    var nrm = new Float32Array(n * 3);
    for (i = 0; i < n; i++) {
      var o = i * 9;
      var ex1 = pos[o + 3] - pos[o], ey1 = pos[o + 4] - pos[o + 1], ez1 = pos[o + 5] - pos[o + 2];
      var ex2 = pos[o + 6] - pos[o], ey2 = pos[o + 7] - pos[o + 1], ez2 = pos[o + 8] - pos[o + 2];
      var nx = ey1 * ez2 - ez1 * ey2, ny = ez1 * ex2 - ex1 * ez2, nz = ex1 * ey2 - ey1 * ex2;
      var len = Math.hypot(nx, ny, nz) || 1;
      nrm[i * 3] = nx / len; nrm[i * 3 + 1] = ny / len; nrm[i * 3 + 2] = nz / len;
    }
    // half-extents per axis (model is centered) — used to fit it into the box
    var ex = 0, ey = 0, ez = 0;
    for (i = 0; i < pos.length; i += 3) {
      if (Math.abs(pos[i]) > ex) ex = Math.abs(pos[i]);
      if (Math.abs(pos[i + 1]) > ey) ey = Math.abs(pos[i + 1]);
      if (Math.abs(pos[i + 2]) > ez) ez = Math.abs(pos[i + 2]);
    }
    return { pos: pos, nrm: nrm, n: n, ex: ex || 1, ey: ey || 1, ez: ez || 1 };
  }

  // ---- build a mesh by EXTRUDING an image's silhouette --------------------
  // Rasterise the image to a mask (alpha if present, else luminance-vs-background),
  // then emit front + back faces (row-run greedy) + side walls (per boundary cell),
  // extruded by `thickness` grid units. Returns the same shape prepMesh() does, so
  // the renderer treats it exactly like an STL model.
  function buildExtrudedMesh(img, thickness, cutout) {
    var MAXD = 120; // mask resolution along the longer side
    var iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
    if (!iw || !ih) { return null; }
    var sc = MAXD / Math.max(iw, ih);
    var gw = Math.max(2, Math.round(iw * sc)), gh = Math.max(2, Math.round(ih * sc));
    var cv = document.createElement('canvas'); cv.width = gw; cv.height = gh;
    var cx = cv.getContext('2d', { willReadFrequently: true });
    cx.drawImage(img, 0, 0, gw, gh);
    var d;
    try { d = cx.getImageData(0, 0, gw, gh).data; } catch (e) { return null; }

    var mask = new Uint8Array(gw * gh), i, transparent = false;
    for (i = 3; i < d.length; i += 4) { if (d[i] < 240) { transparent = true; break; } }
    if (transparent) {
      for (i = 0; i < gw * gh; i++) { mask[i] = d[i * 4 + 3] > 128 ? 1 : 0; }
    } else {
      var lum = function (o) { return 0.299 * d[o] + 0.587 * d[o + 1] + 0.114 * d[o + 2]; };
      var corners = [0, (gw - 1) * 4, (gh - 1) * gw * 4, (gw * gh - 1) * 4], bg = 0;
      for (i = 0; i < 4; i++) { bg += lum(corners[i]); }
      bg /= 4;
      for (i = 0; i < gw * gh; i++) { mask[i] = Math.abs(lum(i * 4) - bg) > 60 ? 1 : 0; }
    }
    // Cutout: erode the silhouette by `cutout` passes so tight counters and
    // letter gaps reopen (a solid condensed wordmark like Metalwood otherwise
    // extrudes into an unreadable black mass). Each pass drops any foreground
    // cell touching background on a side.
    var erode = Math.max(0, Math.round(cutout || 0));
    for (var pass = 0; pass < erode; pass++) {
      var next = new Uint8Array(gw * gh);
      for (var ey = 0; ey < gh; ey++) {
        for (var ex = 0; ex < gw; ex++) {
          if (!mask[ey * gw + ex]) { continue; }
          var keep = ex > 0 && ex < gw - 1 && ey > 0 && ey < gh - 1 &&
            mask[ey * gw + ex - 1] && mask[ey * gw + ex + 1] &&
            mask[(ey - 1) * gw + ex] && mask[(ey + 1) * gw + ex];
          next[ey * gw + ex] = keep ? 1 : 0;
        }
      }
      mask = next;
    }

    function on(x, y) { return x >= 0 && y >= 0 && x < gw && y < gh && mask[y * gw + x]; }

    var V = [];
    function tri(a, b, c) { V.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2]); }
    function quad(p, q, r, s) { tri(p, q, r); tri(p, r, s); }
    var t = thickness || 4, zf = t / 2, zb = -t / 2, gx, gy, x0, x1, Y0, Y1, xx, yy, X, X1, Y, Yt;

    // front (+z) + back (-z), merging horizontal runs per row
    for (gy = 0; gy < gh; gy++) {
      gx = 0;
      while (gx < gw) {
        if (mask[gy * gw + gx]) {
          x0 = gx; while (gx < gw && mask[gy * gw + gx]) { gx++; }
          x1 = gx; Y0 = gh - (gy + 1); Y1 = gh - gy;
          quad([x0, Y0, zf], [x1, Y0, zf], [x1, Y1, zf], [x0, Y1, zf]); // front +z
          quad([x0, Y1, zb], [x1, Y1, zb], [x1, Y0, zb], [x0, Y0, zb]); // back -z
        } else { gx++; }
      }
    }
    // side walls at every exposed cell edge
    for (yy = 0; yy < gh; yy++) {
      for (xx = 0; xx < gw; xx++) {
        if (!mask[yy * gw + xx]) { continue; }
        X = xx; X1 = xx + 1; Y = gh - yy - 1; Yt = Y + 1;
        if (!on(xx - 1, yy)) { quad([X, Y, zf], [X, Yt, zf], [X, Yt, zb], [X, Y, zb]); }   // -x
        if (!on(xx + 1, yy)) { quad([X1, Y, zf], [X1, Y, zb], [X1, Yt, zb], [X1, Yt, zf]); } // +x
        if (!on(xx, yy - 1)) { quad([X, Yt, zf], [X1, Yt, zf], [X1, Yt, zb], [X, Yt, zb]); } // +y
        if (!on(xx, yy + 1)) { quad([X, Y, zf], [X, Y, zb], [X1, Y, zb], [X1, Y, zf]); }     // -y
      }
    }
    if (V.length < 9) { return null; }
    return prepMesh(new Float32Array(V));
  }

  function AsciiLogo(el) {
    this.el = el;
    this.cellW = parseFloat(el.getAttribute('data-cell')) || 3;
    this.fill = parseFloat(el.getAttribute('data-fill')) || 0.46; // fraction of the box the model fills (higher = less slack)
    // appear animation: data-appear="boil" plays a decaying "boil" + fade-in when
    // the model first becomes ready/visible, then settles to the clean logo.
    this.appear = el.getAttribute('data-appear') || '';
    this.boilDur = parseFloat(el.getAttribute('data-boil-duration')) || 1200;
    this.boilMax = isFinite(parseFloat(el.getAttribute('data-boil-amount'))) ? parseFloat(el.getAttribute('data-boil-amount')) : 0.8;
    this.appearStart = 0; this.appearPending = false;
    this.stretch = isFinite(parseFloat(el.getAttribute('data-stretch'))) ? parseFloat(el.getAttribute('data-stretch')) : 1; // extra horizontal scale
    this.align = el.getAttribute('data-align') || 'center'; // 'center' (default) or 'left'
    // motion: 'rock' (gentle left-right sweep — keeps a flat wordmark readable)
    // or 'turntable' (continuous 360° spin). data-spin is deg/sec for turntable.
    this.spinMode = el.getAttribute('data-spin-mode') || 'rock';
    this.spin = isFinite(parseFloat(el.getAttribute('data-spin'))) ? parseFloat(el.getAttribute('data-spin')) : 32;
    this.rockAmp = isFinite(parseFloat(el.getAttribute('data-rock-amp'))) ? parseFloat(el.getAttribute('data-rock-amp')) : 0.7; // rad (~40°)
    this.rockSpeed = isFinite(parseFloat(el.getAttribute('data-rock-speed'))) ? parseFloat(el.getAttribute('data-rock-speed')) : 0.85; // rad/s of the sine
    this.idleSwitch = isFinite(parseFloat(el.getAttribute('data-idle-seconds'))) ? parseFloat(el.getAttribute('data-idle-seconds')) : 9; // sec per idle animation
    this.hoverAmp = isFinite(parseFloat(el.getAttribute('data-hover-amp'))) ? parseFloat(el.getAttribute('data-hover-amp')) : 0.28; // rad of lean toward cursor on hover (0 = off)
    this.hover = false; this.hpx = 0; this.hpy = 0; this.hbx = 0; this.hby = 0;
    // 'difference' = stay high-contrast over any content behind it (white glyphs +
    // mix-blend-mode:difference, like the cash-only header). 'none' = plain colour.
    this.contrast = el.getAttribute('data-contrast') || 'difference';
    this.color = el.getAttribute('data-color') || '';

    // idle animations the logo cycles through while not being dragged. Each maps
    // (phase, amp, speed, restPitch) -> [yawTarget, pitchTarget]; the frame loop
    // eases toward the active one, so switching is smooth.
    this.idleAnims = [
      function (p, a, s, rp) { return [a * Math.sin(p * s), rp]; },                                  // rock — left/right sweep
      function (p, a, s, rp) { return [0.30 * a * Math.sin(p * s * 0.8), rp + 0.22 * Math.sin(p * s * 1.3)]; }, // bob — nod
      function (p, a, s, rp) { return [a * Math.sin(p * s * 0.6), rp + 0.18 * Math.sin(p * s * 0.9 + 1)]; },     // sway — orbit wobble
      function (p, a, s, rp) { return [a * 1.25 * Math.sin(p * s * 0.45), rp + 0.10 * Math.cos(p * s * 0.7)]; }  // turn — lazy turn
    ];
    this.idleIdx = 0; this.idleClock = 0;
    this.forceSize = parseFloat(el.getAttribute('data-size')) || 0;
    this.fps = parseFloat(el.getAttribute('data-fps')) || 30;
    // While the visitor is actively interacting (dragging, or the logo still
    // carries throw velocity) render at this higher rate for a smooth feel, then
    // fall back to the base fps (and, on mobile, freeze after the intro).
    this.fpsActive = 60;
    this.ramp = pickRamp(el);
    this.reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Phones overheat (and iOS Safari crashes) under the continuous glyph
    // re-render. On mobile: render slower, coarser, and — for a turntable
    // logo — spin only a couple of brisk turns then FREEZE (the loop stops),
    // instead of spinning forever. Desktop keeps the always-on look.
    this.isMobile = !!(window.matchMedia && window.matchMedia('(max-width: 749px)').matches);
    this.autoStopTurns = 0;           // turntable: turns before freeze (0 = never)
    this.autoStopMs = 0;              // any mode: ms of animation before freeze (0 = never)
    this.spunYaw = 0; this.autoSpinDone = false; this.animStart = 0;
    if (this.isMobile) {
      this.fps = Math.min(this.fps, 15);
      // All mobile logos (incl. the 'rock' header logo) play a brief intro then
      // FREEZE, instead of animating forever and cooking the phone.
      this.autoStopMs = 8000;
      if (this.spinMode === 'turntable') {
        this.autoStopTurns = 2;
        // brisk intro so 2 turns finish in ~7s rather than ~30s at 22–32°/s
        var dir = this.spin < 0 ? -1 : 1;
        this.spin = Math.max(Math.abs(this.spin), 100) * dir;
      }
    }

    this.canvas = document.createElement('canvas');
    this.canvas.className = 'ascii-logo__canvas';
    el.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d');

    // square offscreen the model is rasterised into, then sampled to chars
    this.model = document.createElement('canvas');
    this.model.width = this.model.height = 200;
    this.mctx = this.model.getContext('2d');
    this.sample = document.createElement('canvas');
    this.sctx = this.sample.getContext('2d', { willReadFrequently: true });

    // orientation + motion state
    this.yaw = 0; this.pitch = 0.22; this.restPitch = 0.22;
    this.vYaw = 0; this.vPitch = 0; this.phase = 0;
    this.scl = 0; this.idleScl = 0; // contain-fit scale (see _resize / _renderModel)
    this.dragging = false;
    this.mesh = null;
    this.running = false;
    this.lastDraw = 0;
    this.lastT = 0;

    this._boundFrame = this._frame.bind(this);
    this._bindInput();
    this._bindLifecycle();

    el.classList.toggle('ascii-logo--off', !isEnabled());

    var self = this;
    var url = el.getAttribute('data-stl');
    var imgSrc = el.getAttribute('data-img');
    if (url) {
      // one shared fetch per STL url — the header overlay, mobile logo and
      // screensaver all load the same ~130KB model (was 3 network requests)
      if (!AsciiLogo._stlCache) { AsciiLogo._stlCache = {}; }
      if (!AsciiLogo._stlCache[url]) {
        AsciiLogo._stlCache[url] = fetch(url).then(function (r) { return r.arrayBuffer(); });
      }
      AsciiLogo._stlCache[url]
        .then(function (buf) {
          self.mesh = prepMesh(parseSTL(buf)); self._resize();
          if (self.appear) { self.appearPending = true; }
          if (isEnabled()) { self._start(); }
        })
        .catch(function () { /* leave the element empty on failure */ });
    } else if (imgSrc) {
      // build the model by extruding an image silhouette (collection logos)
      var thickness = parseFloat(el.getAttribute('data-thickness')) || 4;
      var cutout = parseFloat(el.getAttribute('data-cutout')) || 0;
      var im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = function () {
        var m = buildExtrudedMesh(im, thickness, cutout);
        if (m) { self.mesh = m; self._resize(); if (self.appear) { self.appearPending = true; } if (isEnabled()) { self._start(); } }
      };
      im.src = imgSrc;
    }
  }

  AsciiLogo.prototype.enable = function () {
    this.el.classList.remove('ascii-logo--off');
    if (this.mesh) { this._resize(); this._start(); }
  };
  AsciiLogo.prototype.disable = function () {
    this.el.classList.add('ascii-logo--off');
    this._stop();
  };

  AsciiLogo.prototype._resize = function () {
    var w = this.forceSize || this.el.clientWidth || 64;
    var h = this.forceSize || this.el.clientHeight || w;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.cssW = w; this.cssH = h;
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);
    this.canvas.style.width = w + 'px';
    this.canvas.style.height = h + 'px';
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // character grid — cells are ~2x taller than wide. Slightly coarser on
    // mobile to trim the brief intro's cost (the freeze handles the sustained
    // heat, so this stays close to desktop density for a finer look).
    var cw = this.cellW * (this.isMobile ? 1.15 : 1);
    this.cols = Math.max(2, Math.round(w / cw));
    this.rows = Math.max(2, Math.round(h / (cw * 2)));
    this.sample.width = this.cols; this.sample.height = this.rows;

    // offscreen render canvas matches the BOX aspect, so the model fills the box
    // instead of floating in a square with empty space above/below.
    var RENDER = 260, a = w / h;
    var rw = a >= 1 ? RENDER : Math.round(RENDER * a);
    var rh = a >= 1 ? Math.round(RENDER / a) : RENDER;
    this.model.width = Math.max(8, rw); this.model.height = Math.max(8, rh);

    // idleScl = largest scale that fits every idle-animation orientation (so the
    // logo is full-size + steady at idle; the draw loop shrinks below this only
    // when a drag rotates it into a wider pose).
    if (this.mesh) {
      var M = this.fill, ya = this.rockAmp * 1.3, pr = this.restPitch, ix = 0, iy = 0;
      // turntable spins fully about Y at the rest pitch, so fit the widest yaw
      // pose (front-on) rather than the rock sweep — keeps the logo full-size and
      // hugging the box instead of floating with slack above/below.
      var samples = this.spinMode === 'turntable'
        ? [[0, pr], [Math.PI / 2, pr], [Math.PI, pr], [Math.PI * 1.5, pr]]
        : [[0, pr], [ya, pr], [-ya, pr], [ya, pr + 0.28], [-ya, pr - 0.28], [0, pr + 0.28]];
      for (var s = 0; s < samples.length; s++) {
        var e = this._projExtent(samples[s][0], samples[s][1]);
        if (e[0] > ix) { ix = e[0]; } if (e[1] > iy) { iy = e[1]; }
      }
      this.idleScl = Math.min((this.model.width * M) / ix, (this.model.height * M) / iy);
      if (!this.scl) { this.scl = this.idleScl; }

      // Resizing the canvas above CLEARS it. If the loop is frozen (mobile, after
      // the intro) nothing else would repaint it, so the logo would vanish —
      // iOS fires 'resize' as the URL bar hides/shows on scroll. Repaint now.
      if (!this.running) { this._renderModel(); this._draw(); }
    }
  };

  // Max projected half-extents |x'|,|y'| of the model's bounding box at a given
  // yaw/pitch (8 corners). Used to scale so the model never clips the box.
  AsciiLogo.prototype._projExtent = function (yaw, pitch) {
    var cy = Math.cos(yaw), sy = Math.sin(yaw), cx = Math.cos(pitch), sx = Math.sin(pitch);
    var ex = this.mesh.ex, ey = this.mesh.ey, ez = this.mesh.ez, mx = 0, my = 0;
    for (var i = 0; i < 8; i++) {
      var X = (i & 1) ? ex : -ex, Y = (i & 2) ? ey : -ey, Z = (i & 4) ? ez : -ez;
      var x1 = X * cy + Z * sy, z1 = -X * sy + Z * cy, y2 = Y * cx - z1 * sx;
      if (Math.abs(x1) > mx) { mx = Math.abs(x1); }
      if (Math.abs(y2) > my) { my = Math.abs(y2); }
    }
    return [mx || 1e-3, my || 1e-3];
  };

  // Software-render the mesh at the current yaw/pitch into the model canvas.
  AsciiLogo.prototype._renderModel = function () {
    var rw = this.model.width, rh = this.model.height, c = this.mctx;
    c.clearRect(0, 0, rw, rh);
    c.fillStyle = '#000'; c.fillRect(0, 0, rw, rh);

    var cosY = Math.cos(this.yaw), sinY = Math.sin(this.yaw);
    var cosX = Math.cos(this.pitch), sinX = Math.sin(this.pitch);
    var lx = -0.35, ly = -0.5, lz = 0.79; // light: toward viewer, upper-left
    var mesh = this.mesh, pos = mesh.pos, nrm = mesh.nrm, n = mesh.n, tris = [];

    // UNIFORM contain-fit (keeps true proportions, no stretch). `idleScl` is the
    // size that fits the idle-animation range; while dragging into orientations
    // that would overflow, shrink to fit so the logo never clips the box —
    // instantly when shrinking (no clip), eased when growing back.
    var M = this.fill;
    var fit = this._projExtent(this.yaw, this.pitch);
    var target = Math.min(this.idleScl, (rw * M) / fit[0], (rh * M) / fit[1]);
    if (target < this.scl) { this.scl = target; } else { this.scl += (target - this.scl) * 0.12; }
    var sclX = this.scl * this.stretch, sclY = this.scl;
    // left-align option flushes the wordmark to the left edge of the box (its
    // projected left edge sits at 0); otherwise centred. Always stays flush-left
    // even as the idle animation changes the projected width.
    var cx = (this.align === 'left') ? fit[0] * sclX : rw / 2, cy = rh / 2;

    function rot(x, y, z) {            // yaw about Y, then pitch about X
      var x1 = x * cosY + z * sinY, z1 = -x * sinY + z * cosY;
      var y2 = y * cosX - z1 * sinX, z2 = y * sinX + z1 * cosX;
      return [x1, y2, z2];
    }
    for (var i = 0; i < n; i++) {
      var nr = rot(nrm[i * 3], nrm[i * 3 + 1], nrm[i * 3 + 2]);
      if (nr[2] <= 0) { continue; } // backface
      var o = i * 9;
      var a = rot(pos[o], pos[o + 1], pos[o + 2]);
      var b = rot(pos[o + 3], pos[o + 4], pos[o + 5]);
      var d = rot(pos[o + 6], pos[o + 7], pos[o + 8]);
      var shade = 0.30 + 0.70 * Math.max(0, nr[0] * lx + nr[1] * ly + nr[2] * lz);
      tris.push({
        ax: cx + a[0] * sclX, ay: cy - a[1] * sclY,
        bx: cx + b[0] * sclX, by: cy - b[1] * sclY,
        dx: cx + d[0] * sclX, dy: cy - d[1] * sclY,
        z: a[2] + b[2] + d[2], shade: shade
      });
    }
    tris.sort(function (p, q) { return p.z - q.z; }); // far first
    for (var t = 0; t < tris.length; t++) {
      var tr = tris[t], vv = Math.round(tr.shade * 255);
      c.fillStyle = 'rgb(' + vv + ',' + vv + ',' + vv + ')';
      c.beginPath();
      c.moveTo(tr.ax, tr.ay); c.lineTo(tr.bx, tr.by); c.lineTo(tr.dx, tr.dy); c.closePath();
      c.fill();
    }
  };

  // Sample the model render to the char grid and paint the glyphs.
  AsciiLogo.prototype._draw = function () {
    var cols = this.cols, rows = this.rows, ctx = this.ctx;
    var cellW = this.cssW / cols, cellH = this.cssH / rows;
    ctx.clearRect(0, 0, this.cssW, this.cssH);

    // appear "boil": a decaying noise perturbation + quick fade-in while the
    // logo materialises (data-appear="boil"); settles to the clean render.
    var boilAmp = 0, boilT = 0;
    ctx.globalAlpha = 1;
    if (this.appear === 'boil' && this.appearStart) {
      var ap = (this._now - this.appearStart) / this.boilDur;
      if (ap < 1) {
        boilAmp = (1 - ap) * this.boilMax;
        boilT = Math.floor(this._now / 1000 * 6);
        ctx.globalAlpha = Math.min(1, ap / 0.3);
      }
    }

    // downsample the (black-backed) model render into cols x rows
    this.sctx.clearRect(0, 0, cols, rows);
    this.sctx.drawImage(this.model, 0, 0, cols, rows);
    var data;
    try { data = this.sctx.getImageData(0, 0, cols, rows).data; }
    catch (e) { return; }

    var ramp = this.ramp, rmax = ramp.length - 1;
    ctx.font = 'bold ' + (cellH * 0.98) + 'px ui-monospace, "SF Mono", Menlo, Consolas, monospace';
    ctx.textBaseline = 'top';
    // white glyphs under mix-blend difference invert cleanly against any backdrop;
    // otherwise inherit the element's colour.
    ctx.fillStyle = this.color || (this.contrast === 'difference' ? '#fff' : (getComputedStyle(this.el).color || '#000'));

    for (var y = 0; y < rows; y++) {
      for (var x = 0; x < cols; x++) {
        var i = (y * cols + x) * 4;
        var ink = (0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]) / 255;
        if (boilAmp > 0 && ink > 0.03) { ink += (hash(x, y, boilT) - 0.5) * boilAmp; if (ink < 0) { ink = 0; } }
        if (ink <= 0.06) { continue; } // empty background stays clear
        var ch = ramp.charAt(Math.round(Math.min(1, ink) * rmax));
        if (ch === ' ') { continue; }
        ctx.fillText(ch, x * cellW, y * cellH);
      }
    }
  };

  AsciiLogo.prototype._frame = function (now) {
    if (!this.running) { return; }
    this._raf = requestAnimationFrame(this._boundFrame);
    // interacting (dragging or still coasting from a throw) → high fps; else base
    var interacting = this.dragging || Math.abs(this.vYaw) > 0.1 || Math.abs(this.vPitch) > 0.1;
    var fps = interacting ? this.fpsActive : this.fps;
    if (now - this.lastDraw < 1000 / fps) { return; }
    var dt = this.lastT ? Math.min(0.05, (now - this.lastT) / 1000) : 0.016;
    this.lastDraw = now; this.lastT = now; this._now = now;
    if (this.appearPending) { this.appearStart = now; this.appearPending = false; }

    // mobile: once the intro window elapses, mark the animation done so the
    // loop freezes on the next settled frame (covers 'rock' logos too, not
    // just turntable). A drag re-opens the loop and it re-freezes after.
    if (this.autoStopMs && !this.autoSpinDone) {
      if (!this.animStart) { this.animStart = now; }
      else if (now - this.animStart > this.autoStopMs) { this.autoSpinDone = true; }
    }

    if (!this.dragging) {
      // inertia from the last drag throw, decaying
      this.yaw += this.vYaw * dt; this.pitch += this.vPitch * dt;
      this.vYaw *= 0.93; this.vPitch *= 0.93;
      var settled = Math.abs(this.vYaw) < 0.35 && Math.abs(this.vPitch) < 0.35;
      // hover: a little extra liveliness + ease a small lean toward the cursor
      if (!this.reduced) { this.phase += dt * (this.hover ? 1.5 : 1); }
      var hb = (this.hover && !this.reduced) ? this.hoverAmp : 0;
      this.hbx += (this.hpx * hb - this.hbx) * 0.10;
      this.hby += (this.hpy * hb * 0.6 - this.hby) * 0.10;
      if (this.autoSpinDone && this.autoStopMs) {
        // mobile: intro done — ease to the resting pose (front, turned slightly
        // left), targeting the nearest 2π-equivalent so it settles FORWARD into
        // place rather than unwinding backwards, then freeze (see block below).
        var TWO = 2 * Math.PI;
        var landTgt = Math.round((this.yaw - LANDING_YAW) / TWO) * TWO + LANDING_YAW;
        this.yaw += (landTgt - this.yaw) * 0.14;
        this.pitch += (this.restPitch - this.pitch) * 0.14;
      } else if (this.spinMode === 'turntable') {
        // continuous spin; the throw just adds to it until it decays. On mobile
        // the auto-spin runs autoStopTurns rotations then stops (autoSpinDone).
        if (!this.reduced && !this.autoSpinDone) {
          var dspin = this.spin * Math.PI / 180 * dt;
          this.yaw += dspin;
          if (this.autoStopTurns) {
            this.spunYaw += dspin;
            if (this.spunYaw >= this.autoStopTurns * 2 * Math.PI) { this.autoSpinDone = true; }
          }
        }
        this.pitch += (this.restPitch - this.pitch) * 0.02;
      } else {
        // 'rock' (default): cycle through the idle animations, easing toward the
        // active one once the drag-throw has settled (so switches stay smooth).
        if (!this.reduced) {
          this.idleClock += dt;
          if (this.idleClock > this.idleSwitch) { this.idleClock = 0; this.idleIdx = (this.idleIdx + 1) % this.idleAnims.length; }
        }
        var tgt = this.idleAnims[this.idleIdx](this.phase, this.rockAmp, this.rockSpeed, this.restPitch);
        var k = settled ? 0.05 : 0.0;
        this.yaw += ((tgt[0] + this.hbx) - this.yaw) * k;
        this.pitch += ((tgt[1] + this.hby) - this.pitch) * (settled ? 0.05 : 0.015);
      }
    } else { this.hbx = 0; this.hby = 0; }
    if (this.pitch > 1.4) { this.pitch = 1.4; } else if (this.pitch < -1.4) { this.pitch = -1.4; }

    if (this.mesh) { this._renderModel(); this._draw(); }

    // mobile: once the intro is done, the drag-throw has settled, and it has
    // eased into its resting pose, stop the loop entirely (freeze the frame) —
    // no more heat. A later drag restarts it (see down()). The
    // IntersectionObserver also stops it off-screen.
    if (this.autoSpinDone && this.autoStopMs && !this.dragging && !this.hover &&
        Math.abs(this.vYaw) < 0.2 && Math.abs(this.vPitch) < 0.2) {
      var TWO2 = 2 * Math.PI;
      var lt = Math.round((this.yaw - LANDING_YAW) / TWO2) * TWO2 + LANDING_YAW;
      if (Math.abs(this.yaw - lt) < 0.015 && Math.abs(this.pitch - this.restPitch) < 0.015) {
        this.yaw = lt; this.pitch = this.restPitch;
        this._renderModel(); this._draw();
        this._stop();
      }
    }
  };

  AsciiLogo.prototype._start = function () {
    if (this.running) { return; }
    this.running = true; this.lastT = 0;
    this._raf = requestAnimationFrame(this._boundFrame);
  };
  AsciiLogo.prototype._stop = function () {
    this.running = false;
    if (this._raf) { cancelAnimationFrame(this._raf); }
  };

  // ---- drag-to-orbit (mouse + touch via Pointer Events) -------------------
  AsciiLogo.prototype._bindInput = function () {
    var self = this, last = null;
    this.el.style.touchAction = 'none';
    this.el.style.cursor = 'grab';

    function down(e) {
      self.dragging = true; self.vYaw = 0; self.vPitch = 0;
      self._start(); // a frozen (mobile) logo needs the loop running to orbit
      self.moved = false; self._dsx = e.clientX; self._dsy = e.clientY;
      last = { x: e.clientX, y: e.clientY, t: e.timeStamp || (window.performance && performance.now()) || 0 };
      self.el.style.cursor = 'grabbing';
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      e.preventDefault();
    }
    function move(e) {
      if (!self.dragging || !last) { return; }
      var dx = e.clientX - last.x, dy = e.clientY - last.y;
      if (Math.abs(e.clientX - self._dsx) > 4 || Math.abs(e.clientY - self._dsy) > 4) { self.moved = true; }
      var sens = 0.01;
      self.yaw += dx * sens;
      self.pitch += dy * sens;
      var t = e.timeStamp || (window.performance && performance.now()) || 0;
      var dt = Math.max(1, t - last.t);
      self.vYaw = (dx * sens) / (dt / 1000);   // rad/s, used as release inertia
      self.vPitch = (dy * sens) / (dt / 1000);
      last = { x: e.clientX, y: e.clientY, t: t };
      // re-render immediately for a responsive feel even between fps ticks
      if (self.mesh) { self._renderModel(); self._draw(); }
    }
    function up() {
      self.dragging = false; last = null;
      self.el.style.cursor = 'grab';
      // clamp the thrown velocity so it doesn't whip
      var cap = 6;
      self.vYaw = Math.max(-cap, Math.min(cap, self.vYaw));
      self.vPitch = Math.max(-cap, Math.min(cap, self.vPitch));
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    }
    this.el.addEventListener('pointerdown', down);

    // If the pointer moved (a drag-to-orbit), swallow the click so a wrapping
    // <a> doesn't navigate on release. A genuine click (no movement) passes through.
    this.el.addEventListener('click', function (e) {
      if (self.moved) { e.preventDefault(); e.stopPropagation(); self.moved = false; }
    });

    // hover: track the cursor's position over the logo (-1..1 from centre) so the
    // idle motion can lean toward it a little. Ignored while dragging / on touch.
    function hover(e) {
      if (self.dragging || e.pointerType === 'touch') { return; }
      var r = self.el.getBoundingClientRect();
      self.hpx = Math.max(-1, Math.min(1, ((e.clientX - r.left) / r.width) * 2 - 1));
      self.hpy = Math.max(-1, Math.min(1, ((e.clientY - r.top) / r.height) * 2 - 1));
      self.hover = true;
    }
    function unhover() { self.hover = false; }
    this.el.addEventListener('pointermove', hover);
    this.el.addEventListener('pointerleave', unhover);

    this._teardownInput = function () { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
  };

  AsciiLogo.prototype._bindLifecycle = function () {
    var self = this;
    this._onVis = function () { if (document.hidden) { self._stop(); } else if (self.mesh) { self._start(); } };
    document.addEventListener('visibilitychange', this._onVis);
    this._onResize = function () { clearTimeout(self._rt); self._rt = setTimeout(function () { self._resize(); }, 150); };
    window.addEventListener('resize', this._onResize);
    // pause when scrolled out of view
    if (window.IntersectionObserver) {
      this._io = new IntersectionObserver(function (ents) {
        var vis = ents[0] && ents[0].isIntersecting;
        if (vis && self.mesh && !document.hidden) { self._start(); } else { self._stop(); }
      }, { threshold: 0 });
      this._io.observe(this.el);
    }
  };

  AsciiLogo.prototype.destroy = function () {
    this._stop();
    document.removeEventListener('visibilitychange', this._onVis);
    window.removeEventListener('resize', this._onResize);
    if (this._io) { this._io.disconnect(); }
    if (this._teardownInput) { this._teardownInput(); }
    if (this.canvas.parentNode) { this.canvas.parentNode.removeChild(this.canvas); }
  };

  // ---- effect on/off toggle ----------------------------------------------
  function reflectToggles(on) {
    var btns = document.querySelectorAll('[data-ascii-logo-toggle]');
    for (var i = 0; i < btns.length; i++) {
      btns[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      btns[i].classList.toggle('is-off', !on);
    }
  }
  AsciiLogo.setEnabled = function (on) {
    try { window.localStorage.setItem(EN_KEY, on ? '1' : '0'); } catch (e) {}
    var nodes = document.querySelectorAll('[data-ascii-logo]');
    for (var i = 0; i < nodes.length; i++) {
      var L = nodes[i].__asciiLogo; if (!L) { continue; }
      if (on) { L.enable(); } else { L.disable(); }
    }
    reflectToggles(on);
  };
  AsciiLogo.toggle = function () { AsciiLogo.setEnabled(!isEnabled()); };
  AsciiLogo.isEnabled = isEnabled;

  function initToggles(scope) {
    var btns = (scope || document).querySelectorAll('[data-ascii-logo-toggle]');
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i]; if (b.__asciiToggle) { continue; } b.__asciiToggle = true;
      b.setAttribute('aria-pressed', isEnabled() ? 'true' : 'false');
      b.classList.toggle('is-off', !isEnabled());
      b.addEventListener('click', function (e) { e.preventDefault(); AsciiLogo.toggle(); });
    }
  }

  // ---- bootstrap ----------------------------------------------------------
  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('[data-ascii-logo]');
    for (var i = 0; i < nodes.length; i++) {
      if (!nodes[i].__asciiLogo) { nodes[i].__asciiLogo = new AsciiLogo(nodes[i]); }
    }
    initToggles(scope);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else { initAll(); }

  // Shopify theme editor support
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target.querySelector('[data-ascii-logo]');
    if (el && el.__asciiLogo) { el.__asciiLogo.destroy(); el.__asciiLogo = null; }
  });

  window.AsciiLogo = AsciiLogo;
})();
